import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { filterValues } from 'Helper'
import FilterPill from '../../shared/FilterPill'
import { PERMISSION_CATEGORIES, humanizePermission } from '../permissionCategories'

const slugify = (value) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

function StatusOption({ selected, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 flex items-center gap-4 border border-border-lines rounded-xl p-3 text-left"
    >
      <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${selected ? 'bg-primary' : 'bg-white border border-border-lines'}`}>
        {selected && <UIcons.UilCheck size={14} className="text-white" />}
      </span>
      <span className="text-base text-dark-blue">{label}</span>
    </button>
  )
}

function PermissionCheckbox({ checked, label, onChange }) {
  return (
    <label className="flex items-center gap-4 py-2 cursor-pointer">
      <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${checked ? 'bg-primary' : 'bg-white border border-border-lines'}`}>
        {checked && <UIcons.UilCheck size={16} className="text-white" />}
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} className="hidden" />
      <span className="text-base text-dark-blue">{label}</span>
    </label>
  )
}

export default withTranslation()(function RoleWizard({ t, role, startAtStep = 1, handleClick, onSaved }) {
  const dispatch = useDispatch()
  const [step, setStep] = useState(startAtStep)
  const [permissions, setPermissions] = useState(role?.permissions ?? [])
  const [permissionSearch, setPermissionSearch] = useState('')
  const [module, setModule] = useState('')
  const { data: branchesRes } = useSWR('merchants/branches')
  const branches = branchesRes?.data ?? []

  const method = useForm({ mode: 'all' })
  const { watch, reset, handleSubmit } = method

  useEffect(() => {
    if (role) {
      reset({
        role_name: role.display_name ?? role.name,
        description: role.description,
        status: role.status ?? 'active',
        branch_id: role.branch_id,
        role_level: role.role_level,
        notes: role.notes
      })
    } else {
      reset({ status: 'active' })
    }
  }, [role, reset])

  const [role_name, description] = watch(['role_name', 'description'])
  const status = watch('status') ?? 'active'
  const step1Valid = Boolean(role_name && description)

  const togglePermission = (permission) => {
    setPermissions((prev) => prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission])
  }

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      await apiRequest({
        method: role?.id ? 'put' : 'post',
        url: role?.id ? `user-mgmt/roles/${role.id}` : 'user-mgmt/roles',
        payload: {
          ...payload,
          name: role?.id ? role.name : slugify(payload.role_name),
          display_name: payload.role_name,
          permissions
        }
      })

      toast.success(t(role?.id ? 'settings.role_management.success_update' : 'settings.role_management.success_create'))
      onSaved && onSaved()
      handleClick()
    } catch ({ message }) {
      toast.error(message)
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  const moduleOptions = [
    { label: t('settings.role_management.permissions_step.all_modules'), value: '' },
    ...PERMISSION_CATEGORIES.map((category) => ({ label: t(`settings.role_management.permissions_step.categories.${category.key}`), value: category.key }))
  ]

  const visibleCategories = PERMISSION_CATEGORIES
    .filter((category) => !module || category.key === module)
    .map((category) => ({
      ...category,
      permissions: category.permissions.filter((permission) => humanizePermission(permission).toLowerCase().includes(permissionSearch.trim().toLowerCase()))
    }))
    .filter((category) => category.permissions.length)

  return (
    <div className="fixed inset-0 z-[5000] bg-white flex flex-col">
      <div className="shrink-0 border-b border-border-lines">
        <div className="relative flex items-center justify-center px-6 py-5">
          <button type="button" onClick={handleClick} className="absolute left-6 text-secondary hover:text-dark-blue">
            <UIcons.UilTimes size={22} />
          </button>

          <h3 className="text-xl font-bold text-dark-blue">{t(role?.id ? 'settings.role_management.edit_title' : 'settings.role_management.add_title')}</h3>
        </div>

        <div className="h-1.5 bg-border-lines flex gap-1">
          <div className="h-full bg-primary flex-1" />
          <div className={`h-full flex-1 ${step === 2 ? 'bg-primary' : 'bg-border-lines'}`} />
        </div>
      </div>

      <FormProvider {...method}>
        <form onSubmit={handleSubmit(submit)} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto">
            {
              step === 1 ? (
                <div key="step-1" className="max-w-2xl mx-auto px-6 py-8">
                  <h2 className="text-xl font-semibold text-dark-blue mb-4">{t('settings.role_management.form.title')}</h2>

                  <div className="flex flex-col gap-4">
                    <Input
                      name="role_name"
                      label={t('settings.role_management.form.role_name.label')}
                      placeholder={t('settings.role_management.form.role_name.placeholder')}
                      variant="outlined"
                    />

                    <Input
                      name="description"
                      label={t('settings.role_management.form.description.label')}
                      placeholder={t('settings.role_management.form.description.placeholder')}
                      multiline
                      minRows={3}
                      variant="outlined"
                    />

                    <div>
                      <label className="text-base text-dark-blue inline-block mb-2">{t('settings.role_management.form.status.label')}</label>
                      <div className="flex items-stretch gap-1.5">
                        {
                          ['active', 'inactive'].map((value) => (
                            <StatusOption
                              key={value}
                              selected={status === value}
                              label={t(value === 'active' ? 'settings.role_management.filters.active' : 'settings.role_management.filters.inactive')}
                              onClick={() => method.setValue('status', value)}
                            />
                          ))
                        }
                      </div>
                    </div>

                    <Input
                      name="branch_id"
                      label={t('settings.role_management.form.branch_access.label')}
                      placeholder={t('settings.role_management.form.branch_access.placeholder')}
                      variant="outlined"
                      options={filterValues({ options: branches, param: { value: 'id', label: 'name' } })}
                    />

                    <Input
                      name="role_level"
                      label={t('settings.role_management.form.role_level.label')}
                      placeholder={t('settings.role_management.form.role_level.placeholder')}
                      variant="outlined"
                      options={['branch', 'regional', 'organization'].map((value) => ({ value, label: t(`settings.role_management.form.role_level.options.${value}`) }))}
                    />

                    <Input
                      name="notes"
                      label={t('settings.role_management.form.notes.label')}
                      placeholder={t('settings.role_management.form.notes.placeholder')}
                      multiline
                      minRows={3}
                      variant="outlined"
                    />
                  </div>
                </div>
              ) : (
                <div key="step-2" className="max-w-5xl mx-auto px-6 py-8">
                  <p className="text-center text-secondary mb-6">{t('settings.role_management.permissions_step.title')}</p>

                  <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="relative flex-1 min-w-[220px] max-w-sm">
                      <input
                        type="text"
                        value={permissionSearch}
                        onChange={(event) => setPermissionSearch(event.target.value)}
                        className="w-full bg-white border border-border-lines rounded-full h-9 pl-10 pr-4 text-sm outline-none"
                        placeholder={t('settings.role_management.permissions_step.search_placeholder')}
                      />
                      <UIcons.UilSearch className="text-placeholder absolute top-1/2 -translate-y-1/2 left-3.5" size={16} />
                    </div>

                    <FilterPill
                      label={t('settings.role_management.permissions_step.all_modules')}
                      active={module ? { label: moduleOptions.find((option) => option.value === module)?.label } : null}
                      options={moduleOptions}
                      onSelect={(option) => setModule(option.value)}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    {
                      visibleCategories.map((category) => (
                        <div key={category.key} className="bg-white border border-border-lines rounded-2xl shadow-sm p-6">
                          <p className="text-xl font-semibold text-secondary mb-4">{t(`settings.role_management.permissions_step.categories.${category.key}`)}</p>

                          <div className="grid grid-cols-2 gap-x-4">
                            {
                              category.permissions.map((permission) => (
                                <PermissionCheckbox
                                  key={permission}
                                  checked={permissions.includes(permission)}
                                  label={humanizePermission(permission)}
                                  onChange={() => togglePermission(permission)}
                                />
                              ))
                            }
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )
            }
          </div>

          <div className="shrink-0 border-t border-border-lines px-6 py-5">
            <div className={`mx-auto flex items-center ${step === 1 ? 'max-w-2xl justify-end' : 'max-w-5xl justify-between'}`}>
              {
                step === 1 ? (
                  <Btn
                    content={() => <span className="flex items-center gap-2">{t('misc.next')} <UIcons.UilArrowRight size={16} /></span>}
                    className="h-11 px-6 rounded-full bg-primary text-white disabled:opacity-40"
                    click={() => setStep(2)}
                    disabled={!step1Valid}
                  />
                ) : (
                  <>
                    <Btn
                      content={() => <span className="flex items-center gap-2"><UIcons.UilArrowLeft size={16} /> {t('misc.back')}</span>}
                      className="h-11 px-6 rounded-full border border-border-lines text-dark-blue"
                      click={() => setStep(1)}
                    />

                    <FormBtn
                      content={t('misc.save')}
                      className="h-11 px-6 rounded-full bg-primary text-white disabled:opacity-40"
                    />
                  </>
                )
              }
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
})
